import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, MessageSquare, Send, X, FolderKanban, CheckCircle2, Clock } from "lucide-react";
import { Section, Empty, Pill, ProjectsFilterBar, KpiGrid } from "./CommonUI";
import { WEBSITE_TONE, fmtDate, fmtDateTime } from "./clientUtils";
import { clientFetchComments, clientAddComment, apiErrorMsg } from "@/lib/api";

function LatestUpdateModal({ project, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl max-h-[80vh] rounded-lg shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#ff5722] font-bold">Latest update</div>
            <div className="font-bold text-[#0a0a0a] mt-0.5">{project.name}</div>
          </div>
          <button onClick={onClose} className="text-[#4a4a4a] hover:text-[#0a0a0a]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-5 overflow-auto">
          <p className="text-sm text-[#0a0a0a] whitespace-pre-wrap leading-relaxed">{project.latest_update}</p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-xs font-mono text-[#4a4a4a]">
            <div><span className="text-[#9a9a9a]">ETA:</span> {fmtDate(project.eta)}</div>
            <div><span className="text-[#9a9a9a]">Updated:</span> {fmtDate(project.status_updated_at)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentsDrawer({ project, token, onClose, canPost = true, authorLabel = "You" }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await clientFetchComments(token, project.id)); }
    catch (e) { toast.error(apiErrorMsg(e, "Failed to load comments")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [project.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await clientAddComment(token, project.id, body.trim(), url.trim());
      setBody(""); setUrl("");
      load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Failed to send"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#ff5722] font-bold flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" /> Comments
            </div>
            <div className="font-bold text-[#0a0a0a] mt-0.5">{project.name}</div>
          </div>
          <button onClick={onClose} className="text-[#4a4a4a] hover:text-[#0a0a0a]"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4 space-y-3 bg-[#f7f6f3]">
          {loading ? (
            <div className="text-xs font-mono text-[#4a4a4a]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-xs text-[#4a4a4a]">No comments yet. Start the conversation below.</div>
          ) : items.map((c) => {
            const isMe = c.author_type === "client";
            return (
              <div key={c.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg p-3 ${isMe ? "bg-[#ff5722] text-white" : "bg-white border border-black/10"}`}>
                  <div className={`text-[10px] font-mono uppercase tracking-[0.15em] font-bold mb-1 ${isMe ? "text-white/80" : "text-[#ff5722]"}`}>
                    {c.author_name} · {fmtDate(c.created_at)}
                  </div>
                  <div className="text-sm whitespace-pre-wrap break-words">{c.body}</div>
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noopener noreferrer"
                      className={`mt-1.5 inline-flex items-center gap-1 text-xs font-mono break-all ${isMe ? "text-white underline" : "text-[#ff5722] hover:underline"}`}>
                      <ExternalLink className="h-3 w-3 shrink-0" /> {c.url}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {canPost && (
          <form onSubmit={submit} className="border-t border-black/10 px-5 py-4 space-y-2 bg-white">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2}
              placeholder={`Write a comment as ${authorLabel}…`}
              data-testid="client-comment-body"
              className="w-full border border-black/15 rounded px-3 py-2 text-sm outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20 resize-none" />
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Optional URL (Drive, Figma, etc.)"
              data-testid="client-comment-url"
              className="w-full font-mono text-xs border border-black/15 rounded px-3 py-2 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20" />
            <button type="submit" disabled={busy || !body.trim()} data-testid="client-comment-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] bg-[#0a0a0a] text-white rounded-full hover:bg-[#ff5722] disabled:opacity-60">
              <Send className="h-3.5 w-3.5" /> {busy ? "Posting…" : "Post comment"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function ProjectsTable({ rows, loading, onRefresh, token }) {
  const [q, setQ] = useState("");
  const [webStatus, setWebStatus] = useState("all");
  const [worktype, setWorktype] = useState("all");
  const [updateModal, setUpdateModal] = useState(null);
  const [commentsFor, setCommentsFor] = useState(null);
  const webStatuses = useMemo(() => {
    const set = new Set(rows.map((r) => r.website_status).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [rows]);
  const filtered = useMemo(() => rows.filter((p) => {
    const matchesQ = !q || (`${p.name} ${p.project_type} ${p.latest_update} ${p.live_url}`).toLowerCase().includes(q.toLowerCase());
    const matchesWeb = webStatus === "all" || p.website_status === webStatus;
    const matchesWork = worktype === "all" || (p.worktype || "") === worktype;
    return matchesQ && matchesWeb && matchesWork;
  }), [rows, q, webStatus, worktype]);

  // KPI cards mirror the *filtered* rows so they always reflect what the user sees below.
  const kpis = useMemo(() => {
    const counts = {};
    filtered.forEach((p) => {
      const k = p.website_status || "Unknown";
      counts[k] = (counts[k] || 0) + 1;
    });
    return [
      { label: "Total projects", value: filtered.length,                                                  tone: "bg-blue-500",    icon: <FolderKanban className="h-4 w-4" /> },
      { label: "Planning",       value: counts["Planning"] || 0,                                          tone: "bg-cyan-500",    icon: <Clock className="h-4 w-4" /> },
      { label: "Development",    value: counts["Development"] || counts["Development Server"] || 0,       tone: "bg-sky-500",     icon: <Clock className="h-4 w-4" /> },
      { label: "Testing",        value: counts["Testing"] || counts["Testing Server"] || 0,               tone: "bg-violet-500",  icon: <CheckCircle2 className="h-4 w-4" /> },
      { label: "Live",           value: counts["Live"] || 0,                                              tone: "bg-emerald-500", icon: <CheckCircle2 className="h-4 w-4" /> },
      { label: "Hold",           value: counts["Hold"] || 0,                                              tone: "bg-yellow-500",  icon: <Clock className="h-4 w-4" /> },
    ];
  }, [filtered]);

  return (
    <>
      <KpiGrid cards={kpis} />
      <Section title="Projects" subtitle="Live status of every project we&rsquo;re delivering for you." onRefresh={onRefresh} loading={loading}
        toolbar={<ProjectsFilterBar q={q} onQ={setQ} webStatus={webStatus} onWebStatus={setWebStatus} webOptions={webStatuses}
          worktype={worktype} onWorktype={setWorktype} />}>
      {filtered.length === 0 ? (
        <Empty msg={rows.length === 0 ? "No projects assigned yet. Your account manager will link projects to this account soon." : "No projects match your filter."} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[1100px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-3 w-12">#</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Worktype</th>
                <th className="px-4 py-3">Website Status</th>
                <th className="px-4 py-3">Live URL</th>
                <th className="px-4 py-3">Latest Update</th>
                <th className="px-4 py-3">ETA</th>
                <th className="px-4 py-3">Last updated</th>
                <th className="px-4 py-3">Comments</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} data-testid={`client-project-${p.id}`} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4 font-semibold text-[#0a0a0a]">{p.name}</td>
                  <td className="px-4 py-4 text-sm text-[#4a4a4a]">{p.project_type || "—"}</td>
                  <td className="px-4 py-4">{p.worktype
                    ? <span className={`inline-block whitespace-nowrap px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] rounded ${p.worktype === "Build" ? "bg-blue-100 text-blue-800" : "bg-fuchsia-100 text-fuchsia-800"}`}>{p.worktype}</span>
                    : <span className="text-xs text-[#9a9a9a]">—</span>}</td>
                  <td className="px-4 py-4">{p.website_status ? <Pill cls={WEBSITE_TONE[p.website_status] || "bg-zinc-100 text-zinc-700 border-zinc-300"} label={p.website_status} /> : "—"}</td>
                  <td className="px-4 py-4">
                    {p.live_url ? (
                      <a href={p.live_url.startsWith("http") ? p.live_url : `https://${p.live_url}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#ff5722] hover:underline">
                        <span className="font-mono truncate max-w-[18ch] inline-block">{p.live_url.replace(/^https?:\/\//, "")}</span>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-4 text-sm text-[#0a0a0a] max-w-[28ch]">
                    {p.latest_update ? (
                      <button onClick={() => setUpdateModal(p)} data-testid={`client-update-${p.id}`}
                        className="text-left line-clamp-2 hover:text-[#ff5722] cursor-pointer transition-colors group">
                        <span className="group-hover:underline">{p.latest_update}</span>
                      </button>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-4 text-sm font-mono">{fmtDate(p.eta)}</td>
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]" title={p.updated_at || p.status_updated_at || ""}>{fmtDateTime(p.updated_at || p.status_updated_at)}</td>
                  <td className="px-4 py-4">
                    {(() => {
                      const seenKey = `reachvel_client_seen_${p.id}`;
                      const lastSeen = localStorage.getItem(seenKey) || "";
                      const hasUnread = p.last_comment_author_type === "admin"
                        && p.last_comment_at
                        && p.last_comment_at > lastSeen;
                      return (
                        <button onClick={() => { localStorage.setItem(seenKey, new Date().toISOString()); setCommentsFor(p); }} data-testid={`client-comments-${p.id}`}
                          className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                            hasUnread
                              ? "border-[#ff5722] bg-[#ff5722]/10 text-[#ff5722] font-semibold animate-pulse"
                              : "border-black/15 hover:border-[#ff5722] hover:text-[#ff5722]"
                          }`}>
                          <MessageSquare className="h-3.5 w-3.5" /> {hasUnread ? "New" : "Open"}
                          {hasUnread && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#ff5722] ring-2 ring-white" />}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {updateModal && <LatestUpdateModal project={updateModal} onClose={() => setUpdateModal(null)} />}
      {commentsFor && <CommentsDrawer project={commentsFor} token={token} canPost authorLabel="You" onClose={() => setCommentsFor(null)} />}
    </Section>
    </>
  );
}
